/**
 * Cart gift guard — removes a stale free-gift line (task 131321 / SALE-02).
 *
 * A free-gift voucher (e.g. NEWSHOP: free gift above CHF 80) zeroes the
 * gift's line while the cart qualifies, but when the cart drops below the
 * threshold Shopify keeps the line and reprices it at full value. Checkout
 * drops it correctly; the cart page/drawer show a paid CHF 20 line the
 * customer never chose to buy.
 *
 * Two ways to recognise that line:
 *
 * 1. The curaprox-gift-app checkout extension adds the gift with the line
 *    property `_freeGiftBy: curaprox-gift-app` (its MARKER_KEY / MARKER_VALUE),
 *    and the property stays on the line once it goes stale (verified
 *    2026-09-16 on fdxamf-rd: `final_line_price: 2000,
 *    line_level_total_discount: 0`, marker still set). Such a line is removed
 *    as soon as it carries no discount, whatever this tab saw before. Relying
 *    on memory alone missed it when the cart page the shopper returned to had
 *    never loaded the cart while the gift was free: a page restored with the
 *    Back button, or a second tab (sessionStorage is per tab).
 * 2. A native Buy-X-get-Y line carries NO marker (verified 2026-09-01:
 *    `discounts: []`, empty properties), so the guard remembers which variants
 *    were fully free (final_line_price === 0 with a line-level discount) in
 *    sessionStorage and removes such a line when it later shows up
 *    undiscounted. Deliberately narrow: a customer who adds a paid unit of
 *    the same product merges the line (final > 0), which the guard then
 *    ignores — over-removal is worse than a rare leftover.
 *
 * Removal sets the line's quantity to 0 and dispatches a `cart:update` so the
 * cart page / drawer re-render themselves (component-cart-items.js re-renders
 * via sectionRenderer when the event carries no section HTML).
 */
(function () {
  var KEY = 'cura:gift-lines';
  var GIFT_MARKER_KEY = '_freeGiftBy';
  var GIFT_MARKER_VALUE = 'curaprox-gift-app';
  var busy = false;

  function isAppGift(item) {
    return !!item.properties && item.properties[GIFT_MARKER_KEY] === GIFT_MARKER_VALUE;
  }

  function readKnown() {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function writeKnown(known) {
    try {
      // Only keep the key while there is something to remember. Writing `{}`
      // on every page view made the entry show up on Cookiebot's scanner for
      // every visitor (empty cart included); with this it exists only while a
      // free gift line is in the cart.
      if (Object.keys(known).length) {
        sessionStorage.setItem(KEY, JSON.stringify(known));
      } else {
        sessionStorage.removeItem(KEY);
      }
    } catch (e) {
      /* private mode etc. — guard degrades to a no-op */
    }
  }

  function scan() {
    if (busy) return;
    busy = true;
    fetch(window.Theme && Theme.routes && Theme.routes.cart_url ? Theme.routes.cart_url + '.js' : '/cart.js', {
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var known = readKnown();
        var next = {};
        var stale = null;

        cart.items.forEach(function (item) {
          // Track by variant id, NOT item.key: the key's hash suffix changes when
          // the line's discount state changes (verified: ":cee98…" while free →
          // ":e8366…" once stale), so a remembered key never matches again.
          var free = item.final_line_price === 0 && item.line_level_total_discount > 0;
          var undiscounted = item.final_line_price > 0 && item.line_level_total_discount === 0;
          if (free) {
            next[item.variant_id] = true;
          } else if (undiscounted && (isAppGift(item) || known[item.variant_id])) {
            // The app's gift line, or a fully-free gift earlier in this
            // session, now charged in full — remove via the line's CURRENT key.
            stale = item.key;
          }
        });
        writeKnown(next);

        if (!stale) return null;
        var changeUrl =
          (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change';
        return fetch(changeUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: stale, quantity: 0 }),
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (updated) {
            if (updated && updated.errors) return;
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: updated,
                  sourceId: 'cart-gift-guard',
                  data: { source: 'cart-gift-guard', itemCount: updated && updated.item_count },
                },
              })
            );
          });
      })
      .catch(function () {
        /* network hiccup — try again on the next cart:update */
      })
      .finally(function () {
        busy = false;
      });
  }

  document.addEventListener('cart:update', function (event) {
    var source = event && event.detail && event.detail.data && event.detail.data.source;
    if (source === 'cart-gift-guard') return; // our own dispatch — already rescanned
    scan();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
